import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS: Record<string, { label: string; cls: string }> = {
  aan_te_melden: { label: 'Aan te melden bij Allianz', cls: 'bg-amber-100 text-amber-700' },
  aangemeld: { label: 'Aangemeld — wacht op Allianz', cls: 'bg-mfb-blue/10 text-mfb-blue' },
  geldig: { label: 'Geldig', cls: 'bg-emerald-100 text-emerald-700' },
  verlopen: { label: 'Verlopen', cls: 'bg-red-100 text-red-700' },
  geen_opinie: { label: 'Geen opinie', cls: 'bg-slate-100 text-slate-600' },
};

export const AdminKredietopiniesPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };
  const notify = useToast();

  const [colls, setColls] = useState<any[]>([]);
  const [cid, setCid] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [toonArchief, setToonArchief] = useState(false);
  const [filter, setFilter] = useState('alle');
  const [vals, setVals] = useState<Record<string, { eh: string; grade: string }>>({});

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    fetch(`${API_BASE_URL}/api/settlement/collectivities-list`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then((d) => { setColls(d); if (d[0]) setCid(d[0].id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken]);

  const load = useCallback(() => {
    if (!cid) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/admin/kredietopinies-detail?collectivity_id=${cid}`, { headers, cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : { rijen: [] })).then((d) => setRows(d.rijen || [])).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, accessToken]);
  useEffect(() => { load(); }, [load]);

  const zichtbaar = toonArchief ? rows : rows.filter((r) => !r.gearchiveerd);
  const tel = (s: string) => zichtbaar.filter((r) => r.status === s).length;
  const getoond = filter === 'alle' ? zichtbaar : zichtbaar.filter((r) => r.status === filter);

  const aanmelden = async (id: string) => {
    setBusy(id);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/debtors/${id}/allianz-aanmelden`, { method: 'POST', headers });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || ''); }
      const d = await r.json();
      notify.success(d.email_verzonden
        ? `Registratieverzoek verstuurd naar ${d.ontvanger}.`
        : 'Debiteur aangemeld — e-mail niet verzonden (SMTP niet ingesteld).', 'Aangemeld bij Allianz');
      load();
    } catch (e) { notify.error(e instanceof Error && e.message ? e.message : 'Aanmelden mislukt.'); } finally { setBusy(''); }
  };

  const koppel = async (id: string) => {
    const v = vals[id] || { eh: '', grade: '' };
    if (!v.eh.trim()) { notify.error('Vul een EulerHermes-ID in.'); return; }
    setBusy(id);
    try {
      const body: any = { euler_hermes_id: v.eh.trim() };
      if (v.grade) body.grade = Number(v.grade);
      const r = await fetch(`${API_BASE_URL}/api/admin/debtors/${id}/euler-hermes`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      notify.success('Debiteur gekoppeld aan Allianz Trade en doorgezet naar het lid.', 'Gekoppeld');
      load();
    } catch { notify.error('Koppelen mislukt.'); } finally { setBusy(''); }
  };

  const sync = async (id: string) => {
    setBusy(id);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/debtors/${id}/kredietcheck`, { method: 'POST', headers });
      if (!r.ok) throw new Error();
      const d = await r.json();
      notify.success(d.match_status === 'niet_gevonden'
        ? 'Debiteur (nog) niet gevonden bij Allianz — aanmelden vereist.'
        : `Kredietcheck uitgevoerd — grade ${d.grade ?? '—'}.`, 'Allianz-sync');
      load();
    } catch { notify.error('Synchroniseren mislukt.'); } finally { setBusy(''); }
  };

  const archiveer = async (id: string, gearchiveerd: boolean) => {
    setBusy(id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/archiveren/debiteur/${id}`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ gearchiveerd }),
      });
      if (!res.ok) throw new Error();
      notify.success(gearchiveerd ? 'Naar archief verplaatst.' : 'Teruggehaald uit archief.');
      load();
    } catch { notify.error('Archiveren mislukt.'); } finally { setBusy(''); }
  };

  return (
    <Layout>
      <div className="mb-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold text-mfb-blue">Kredietopinies</h1>
          <p className="text-sm text-slate-500 max-w-3xl">
            Debiteuren die leden in hun portaal opvoeren maar die (nog) niet in de Allianz Trade-database staan, komen hier in een
            manuele bak. Meld ze eerst aan bij Allianz (automatische e-mail); zodra ze zijn opgevoerd koppel je het EulerHermes-ID en
            haal je de grade op. Geldige en verlopen opinies zie je hier ook terug.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={toonArchief} onChange={(e) => setToonArchief(e.target.checked)} /> Toon archief
          </label>
          <select value={cid} onChange={(e) => setCid(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
            {colls.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <Card label="Aan te melden" value={tel('aan_te_melden')} tone="amber" onClick={() => setFilter(filter === 'aan_te_melden' ? 'alle' : 'aan_te_melden')} active={filter === 'aan_te_melden'} />
        <Card label="Aangemeld (wacht)" value={tel('aangemeld')} tone="blue" onClick={() => setFilter(filter === 'aangemeld' ? 'alle' : 'aangemeld')} active={filter === 'aangemeld'} />
        <Card label="Geldig" value={tel('geldig')} tone="emerald" onClick={() => setFilter(filter === 'geldig' ? 'alle' : 'geldig')} active={filter === 'geldig'} />
        <Card label="Verlopen" value={tel('verlopen')} tone="red" onClick={() => setFilter(filter === 'verlopen' ? 'alle' : 'verlopen')} active={filter === 'verlopen'} />
        <Card label="Geen opinie" value={tel('geen_opinie')} tone="slate" onClick={() => setFilter(filter === 'geen_opinie' ? 'alle' : 'geen_opinie')} active={filter === 'geen_opinie'} />
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : getoond.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">Geen debiteuren in deze selectie.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50"><tr>
              {['Lid / debiteur', 'EulerHermes-ID', 'Grade', 'Geldig t/m', 'Status', 'Actie'].map((h) => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {getoond.map((r) => {
                const st = STATUS[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-600' };
                const v = vals[r.debtor_id] || { eh: '', grade: '' };
                const manueel = r.status === 'aan_te_melden' || r.status === 'aangemeld';
                return (
                  <tr key={r.debtor_id} className="hover:bg-slate-50 align-top">
                    <td className="px-5 py-3">
                      <div className="font-medium text-mfb-blue">{r.debiteur}</div>
                      <div className="text-xs text-slate-400">{r.lid || '—'} · {r.land}{r.kvk ? ` · reg. ${r.kvk}` : ''}</div>
                      {manueel && r.adres && <div className="text-xs text-slate-400">{r.adres}{r.contact_email ? ` · ${r.contact_email}` : ''}</div>}
                    </td>
                    <td className="px-5 py-3 text-slate-600">{r.euler_hermes_id || '—'}</td>
                    <td className="px-5 py-3 text-slate-700">{r.grade ?? '—'}{r.grade != null && <span className={`ml-1 text-[10px] ${r.verzekerbaar ? 'text-emerald-600' : 'text-red-600'}`}>{r.verzekerbaar ? 'verz.' : 'niet'}</span>}</td>
                    <td className="px-5 py-3 text-slate-600">{r.geldig_tot || '—'}</td>
                    <td className="px-5 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                      {r.status === 'aangemeld' && r.aangemeld_op && <div className="text-[10px] text-slate-400 mt-0.5">aangemeld {r.aangemeld_op}</div>}
                      {r.gearchiveerd && <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 text-slate-600">archief</span>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        {r.status === 'aan_te_melden' ? (
                          <button disabled={busy === r.debtor_id} onClick={() => aanmelden(r.debtor_id)} className="px-3 py-1.5 text-xs font-semibold text-white bg-mfb-orange hover:bg-orange-600 rounded-lg disabled:opacity-50 whitespace-nowrap">Aanmelden bij Allianz</button>
                        ) : r.status === 'aangemeld' ? (
                          <>
                            <input placeholder="EH-ID" value={v.eh} onChange={(e) => setVals({ ...vals, [r.debtor_id]: { ...v, eh: e.target.value } })} className="w-28 px-2 py-1 border border-slate-300 rounded text-sm" />
                            <input placeholder="grade" type="number" value={v.grade} onChange={(e) => setVals({ ...vals, [r.debtor_id]: { ...v, grade: e.target.value } })} className="w-16 px-2 py-1 border border-slate-300 rounded text-sm" />
                            <button disabled={busy === r.debtor_id} onClick={() => koppel(r.debtor_id)} className="px-3 py-1.5 text-xs font-semibold text-white bg-mfb-blue hover:bg-mfb-blue-dark rounded-lg disabled:opacity-50 whitespace-nowrap">Koppelen (EulerID + grade)</button>
                          </>
                        ) : (
                          <button disabled={busy === r.debtor_id} onClick={() => sync(r.debtor_id)} className="px-3 py-1.5 text-xs font-semibold text-mfb-blue border border-mfb-blue/40 hover:bg-mfb-blue/5 rounded-lg disabled:opacity-50 whitespace-nowrap">
                            {r.status === 'verlopen' ? 'Vernieuwen (sync)' : r.status === 'geen_opinie' ? 'Kredietcheck (sync)' : 'Opnieuw (sync)'}
                          </button>
                        )}
                        {r.gearchiveerd
                          ? <button disabled={busy === r.debtor_id} onClick={() => archiveer(r.debtor_id, false)} className="px-2.5 py-1.5 text-xs font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg disabled:opacity-50">Terughalen</button>
                          : <button disabled={busy === r.debtor_id} onClick={() => archiveer(r.debtor_id, true)} className="px-2.5 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg disabled:opacity-50" title="Naar archief">Archiveer</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}
    </Layout>
  );
};

function Card({ label, value, tone, onClick, active }: { label: string; value: number; tone: 'emerald' | 'red' | 'slate' | 'amber' | 'blue'; onClick: () => void; active: boolean }) {
  const map: Record<string, string> = {
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    red: 'text-red-700 bg-red-50 border-red-200',
    slate: 'text-slate-600 bg-slate-50 border-slate-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    blue: 'text-mfb-blue bg-mfb-blue/5 border-mfb-blue/20',
  };
  return (
    <button type="button" onClick={onClick}
      className={`text-left rounded-xl border p-4 transition hover:shadow-md hover:-translate-y-0.5 ${map[tone]} ${active ? 'ring-2 ring-mfb-blue' : ''}`}>
      <div className="text-2xl font-display font-bold tabular-nums">{value}</div>
      <div className="text-xs font-medium mt-0.5">{label}</div>
    </button>
  );
}

export default AdminKredietopiniesPage;
